import { Avatar, Box, Button, TablePagination, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import AuditDiscussionViewer from './AuditDiscussionViewer'
import api from '../../../service/api'

import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import noAssignmentImg from '../../../assets/noassignment.svg'
import { formatDate } from '../../../utils/utilities'

const AuditDiscussionsList = () => {

    const [searchParams, setSearchParams] = useSearchParams( { searchText: "", month: "", selectedInstitute: "all", view: "tabular", sortByName: "1", sortBySubmissionsCount: "1", sortByAssignmentCount: "1" } )
    const searchText = searchParams.get( 'searchText' ) || ""
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"

    const [page, setPage] = useState( 0 )
    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [discussions, setDiscussions] = useState( [] )
    const [selectedDiscussion, setSelectedDiscussion] = useState( null )
    const [rowsPerPage, setRowsPerPage] = useState( +( localStorage.getItem( 'adminAuditRowsPerPage' ) || 50 ) )

    const { border, palette, table, layout } = useTheme()

    const navigate = useNavigate()
    const { pathname } = useLocation()

    const filterFunction = item => {
        const searchValue = new RegExp( searchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' )
        return item?.created_by_data?.branch_name?.match( searchValue ) || item?.creator_data?.name?.match( searchValue ) || item.created_by?.match( searchValue )
    }

    const removeAppliedFilters = () => {
        setSearchParams( prev => {
            prev.set( 'searchText', '' )
            prev.set( 'selectedInstitute', '-' )
            prev.set( 'selectedDepartment', '-' )
            return prev
        } )
    }

    const handleDialogClose = () => {
        setSelectedDiscussion( null )
    }

    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data } } = await api.audit.getDiscussionsForAuditing( { institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            setDiscussions( data )
            setError( null )
        } catch ( err ) {
            console.log( err )
            setError( { code: err?.response?.status, essage: err?.message?.data?.message || "Something went wrong while fetching discussions." } )
        } finally {
            setLoading( false )
        }
    }, [selectedInstitute] )

    useEffect( () => {
        getData()
    }, [getData] )


    return (
        <Box sx={{ flexShrink: 0, paddingBottom: "20px", display: "flex", flexDirection: "column", gap: layout.cardsGap }}>

            {selectedDiscussion && <AuditDiscussionViewer discussion={selectedDiscussion} handleClose={handleDialogClose} openState={Boolean( selectedDiscussion )} />}

            <Box display="flex" gap="20px" alignItems="flex-end" justifyContent="space-between" >
                <Typography variant="h1" fontWeight="600" component="h2" fontSize="14px">Discussions</Typography>
                <Button size="small" onClick={() => navigate( `/${pathname.includes( "admin" ) ? "admin" : "audit"}/discussions` )} variant="outlined" color='secondary' disableElevation >View Stats</Button>
            </Box >

            {!error && !loading && discussions.filter( filterFunction ).length > 0 && <Box overflow="hidden">
                <Box overflow="clip" >
                    <Box display="flex" overflow="auto" className="custom-scrollbar" maxHeight="500px" flexDirection="column" gap="10px" >
                        {discussions.filter( filterFunction ).slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage ).map( ( row, index ) => {
                            return <Box borderRadius="5px" alignItems="flex-start" sx={{ cursor: "pointer" }} onClick={() => setSelectedDiscussion( row )} padding="10px 10px 15px 10px" bgcolor={palette.contentBg} display="flex" gap="10px" border={border[1]} key={row._id}>
                                <Box marginTop="5px">
                                    <Tooltip title={row.creator_data.name}>
                                        <Avatar sx={{ width: "30px", height: "30px" }} src={row.creator_data?.user_profile_image_url} >{row.creator_data.name[0]}</Avatar>
                                    </Tooltip>
                                </Box>
                                <Box>
                                    <Typography variant='h6' component="p" fontSize="14px">{row.title}</Typography>
                                    <Typography variant='subtitle2' color="textSecondary" fontSize="12px">@{row.creator_data.name}, On: {formatDate( row.created_at, { month: "short" } )}</Typography>
                                </Box>
                            </Box>
                        } )}
                    </Box>
                    <TablePagination
                        sx={{ background: palette.contentBg, position: "sticky", bottom: 0 }}
                        rowsPerPageOptions={[50, 100, 150, 200]}
                        labelRowsPerPage="Discussions per page"
                        component="div"
                        count={discussions.filter( filterFunction ).length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={( event, val ) => setPage( val )}
                        onRowsPerPageChange={( event ) => {
                            setRowsPerPage( parseInt( event.target.value, 10 ) )
                            localStorage.setItem( 'adminAuditRowsPerPage', event.target.value )
                            setPage( 0 )
                        }}
                    />
                </Box>
            </Box>}

            {!loading && !error && discussions.filter( filterFunction ).length === 0 && <Box flexDirection="column" padding="20px" display="flex" alignItems="center" justifyContent="center" gap="20px">
                <Box component="img" src={noAssignmentImg} height="120px" alt="No assignmets to show" />
                <Typography textAlign="center" variant='subtitle2'>No discussions were found for applied filters!</Typography>
                <Button onClick={removeAppliedFilters} sx={{ textTransform: "capitalize" }} color="secondary" variant="contained" disableElevation>Remove filters</Button>
            </Box>}
            {loading && !error && <DataLoadingSpinner padding='0' waitingMessage="Loading discussions please wait...." />}
            {error && <TryAgainMessageBlock err={error.message} code={error.code} loading={loading} getData={getData} />}
        </Box>
    )
}

export default AuditDiscussionsList
